import React, { useState } from 'react';
import {
	Typography,
	Box,
	CircularProgress,
	IconButton,
} from '@material-ui/core';
import VirtualizedTable from '../ReactVirtualized/ReactVirtualized';
import { ThemeProvider, makeStyles } from '@material-ui/core/styles';
import { theme } from '../../../theme/theme';
import { useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { colors } from '../../../theme/color-palette';
import {
	ERROR_SCREEN_MSG_STORE,
	DATA_NOT_AVAILABLE,
} from '../../../constants/constant';
import ErrorScreenMessage from '../../ErrorScreenMessage/ErrorScreenMessage';
import DownloadTable from '../../Download/DownloadTable';
import ToggleFilter from '../../Filter/ToggleFilter';
import { getColumns } from './utils';
import { getKPIName } from './../../../helper/commonUtils';
import { useSelector } from 'react-redux';

const { LIGHT_GREY, FONT_COLOR, PURPLE } = colors;

// Styles
const useStyles = makeStyles(() => ({
	container: {
		width: '100%',
		border: (props) => props.border,
		borderRadius: (props) => props.borderRadius,
		overflowY: 'hidden',
		paddingBottom: '16px',
		minHeight: '100px',
	},
	errorContainer: {
		width: '100%',
		height: '472px',
		overflowY: 'hidden',
		overflowX: 'hidden',
		paddingBottom: '25px',
		minHeight: '100px',
	},
	header: {
		position: 'relative',
		display: 'flex',
		justifyContent: 'space-between',
		alignItem: 'center',
	},
	sortIcon: {
		position: 'absolute',
		bottom: 12,
		right: 40,
		color: PURPLE,
	},
	title: {
		textAlign: 'left',
		fontSize: '20px',
		color: FONT_COLOR,
		fontFamily: 'Bogle-Regular',
		padding: '16px',
	},
	loaderContainer: {
		height: '422px',
		display: 'flex',
	},
	circularProgress: {
		margin: 'auto',
	},
	toggle: {
		margin: 0,
		display: 'flex',
		alignItems: 'center',
	},
}));

const MuiTableContainer = (props) => {
	const {
		tableHeaders,
		tableRows,
		title,
		handleRowClick,
		category,
		weekFilter,
		selectedFilter,
		selectedTab,
		options,
		selectedOption_,
		isLoading,
		dataError,
		setValue,
		brandName,
		upcNumber,
		showHeaders,
		height,
		borderRadius,
		border,
		isFourColumns,
		fontSize,
		paddingLeft,
		paddingRight,
		subHeaders,
		marginLeft,
		numberOfRecords,
		isStore,
	} = props;

	const isNotSmallScreen = useMediaQuery('(min-width:700px)');
	const [sort, setSort] = useState(true);
	const [selectedOption, setSelectedOption] = useState(selectedOption_);
	const classes = useStyles({
		sort,
		borderRadius: borderRadius,
		border: border,
	});
	const kpiName = useSelector((state) => state.salesReducer.kpiName);
	const kpiType = getKPIName(kpiName);

	const handleClick = () => {
		setSort(!sort);
	};
	const getErrorMsg = () => {
		return dataError === 0 ? DATA_NOT_AVAILABLE : ERROR_SCREEN_MSG_STORE;
	};

	const gettableContent = () => {
		const changeToggle = (value) => {
			setSelectedOption(value);
			setValue(value);
		};

		return (
			<ThemeProvider theme={theme}>
				{showHeaders && (
					<Box className={classes.header}>
						<Typography className={classes.title}>{title}</Typography>
						{isNotSmallScreen && (
							<Box className={classes.toggle}>
								{options?.length !== 0 && (
									<Typography>
										<ToggleFilter
											options={options}
											selectedBtn={selectedOption}
											onChange={changeToggle}
										/>
									</Typography>
								)}

								<Typography className={classes.download}>
									<DownloadTable
										rowData={
											Array.isArray(tableRows)
												? tableRows
												: [...(tableRows?.topList || {})]
										}
										headers={getColumns(tableHeaders, selectedFilter)}
										chartTitle={title}
										category={category}
										weekFilter={weekFilter}
										selectedFilter={selectedFilter}
										selectedTab={selectedTab}
										brandName={brandName}
										upcNumber={upcNumber}
										kpiType={kpiType}
									/>
								</Typography>
							</Box>
						)}

						{!isNotSmallScreen && (
							<Box>
								<IconButton
									id="back-to-list"
									aria-label="back-button"
									size="medium"
									className={classes.sortIcon}
									onClick={handleClick}>
									{sort ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
								</IconButton>
							</Box>
						)}
					</Box>
				)}

				{!tableRows?.length ? (
					<Box className={classes.errorContainer}>
						<ErrorScreenMessage
							errorMsg={getErrorMsg()}
							chartTitle={title}
							isMuiTableContainer={true}
						/>
					</Box>
				) : (
					<VirtualizedTable
						data={tableRows}
						height={height}
						width={'100%'}
						columns={tableHeaders}
						initialSortKey={kpiType}
						handleRowClick={handleRowClick}
						externalSortClick={sort}
						isItemTab={true}
						valueUnit={selectedOption}
						selectedTab={selectedTab}
						isFourColumns={isFourColumns}
						fontSize={fontSize}
						paddingLeft={paddingLeft}
						paddingRight={paddingRight}
						subHeaders={subHeaders}
						marginLeft={marginLeft}
						numberOfRecords={numberOfRecords}
						isStore={isStore}
					/>
				)}
			</ThemeProvider>
		);
	};

	return (
		<Box className={classes.container}>
			{isLoading ? (
				<Box className={classes.loaderContainer}>
					<CircularProgress className={classes.circularProgress} />
				</Box>
			) : (
				gettableContent()
			)}
		</Box>
	);
};

MuiTableContainer.defaultProps = {
	tableRows: [],
	tableHeaders: [],
	options: [],
	showHeaders: true,
	height: 425,
	showBorder: true,
	borderRadius: '8px',
	border: `1px solid ${LIGHT_GREY}`,
	isFourColumns: false,
	fontSize: '16px',
	paddingRight: '16px',
	paddingLeft: '16px',
	subHeaders: false,
	marginLeft: '6px',
	numberOfRecords: 0,
};

MuiTableContainer.propTypes = {
	tableRows: PropTypes.arrayOf(PropTypes.shape({})),
	tableHeaders: PropTypes.arrayOf(PropTypes.shape({})),
	title: PropTypes.string.isRequired,
	handleRowClick: PropTypes.func.isRequired,
	category: PropTypes.string,
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	selectedFilter: PropTypes.string,
	selectedTab: PropTypes.string,
	options: PropTypes.arrayOf(PropTypes.string),
	selectedOption_: PropTypes.string,
	isLoading: PropTypes.bool,
	dataError: PropTypes.number,
	setValue: PropTypes.func,
	brandName: PropTypes.string,
	upcNumber: PropTypes.string,
	showHeaders: PropTypes.bool,
	height: PropTypes.string,
	borderRadius: PropTypes.string,
	border: PropTypes.string,
	isFourColumns: PropTypes.bool,
	fontSize: PropTypes.string,
	paddingLeft: PropTypes.string,
	paddingRight: PropTypes.string,
	subHeaders: PropTypes.bool,
	marginLeft: PropTypes.string,
	numberOfRecords: PropTypes.number,
	isStore: PropTypes.bool,
};

export default MuiTableContainer;
